import { useEffect, useRef, useState, useTransition } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MoreVertical,
  Eye,
  Pencil,
  Users,
  UserPlus,
  Trash2,
} from "lucide-react";
import { useDict } from "@/lib/i18n/context";

export function JobActions({ jobId }: { jobId: string }) {
  const t = useDict();
  const [open, setOpen] = useState(false);
  const [pending, startTransition] = useTransition();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  function remove() {
    setOpen(false);
    if (!confirm(t.jobs.deleteConfirm)) return;
    startTransition(async () => {
      await fetch(`/api/jobs/${jobId}`, {
        method: "DELETE",
        credentials: "include",
      });
      window.location.reload();
    });
  }

  const itemCls =
    "flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground/80 hover:bg-surface";

  return (
    <div className="relative inline-block text-start" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={pending}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card text-muted hover:bg-surface disabled:opacity-60"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute end-0 z-20 mt-1 w-48 overflow-hidden rounded-xl border border-border bg-card py-1 shadow-lg"
        >
          <Link to={`/jobs/${jobId}`} className={itemCls} role="menuitem">
            <Eye className="h-4 w-4" /> {t.jobs.view}
          </Link>
          <Link to={`/jobs/${jobId}?edit=1`} className={itemCls} role="menuitem">
            <Pencil className="h-4 w-4" /> {t.jobs.edit}
          </Link>
          <Link to={`/candidates?job=${jobId}`} className={itemCls} role="menuitem">
            <Users className="h-4 w-4" /> {t.jobs.viewCandidates}
          </Link>
          <Link
            to={`/candidates/new?job=${jobId}`}
            className={itemCls}
            role="menuitem"
          >
            <UserPlus className="h-4 w-4" /> {t.jobs.inviteCandidate}
          </Link>
          <button
            type="button"
            onClick={remove}
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" /> {t.jobs.delete}
          </button>
        </div>
      )}
    </div>
  );
}
